/**
 * Helpers for building Roomnexa User Guide pages.
 * Keep content practical — every guide maps to a real product surface.
 */

export const callout = (variant, title, body) => ({
  type: 'callout',
  variant, // tip | note | warning | success | important
  title,
  body,
});

export const steps = (items) => ({
  type: 'steps',
  items: items.map((item) =>
    typeof item === 'string' ? { text: item } : item
  ),
});

export const paragraphs = (...texts) =>
  texts.filter(Boolean).map((text) => ({ type: 'paragraph', text }));

export const bullets = (items, title) => ({
  type: 'list',
  ordered: false,
  title: title || null,
  items,
});

export const numbered = (items, title) => ({
  type: 'list',
  ordered: true,
  title: title || null,
  items,
});

export const table = (headers, rows, caption) => ({
  type: 'table',
  headers,
  rows,
  caption: caption || null,
});

export const workflow = (title, nodes) => ({
  type: 'workflow',
  title,
  nodes,
});

export const faq = (items) => ({
  type: 'faq',
  items,
});

export const imagePlaceholder = (label, caption) => ({
  type: 'imagePlaceholder',
  label,
  caption: caption || null,
});

export const video = (youtubeId, title, caption) => ({
  type: 'video',
  youtubeId,
  title: title || null,
  caption: caption || null,
});

export const heading = (text, level = 3) => ({
  type: 'heading',
  level,
  text,
});

export const fieldTable = (rows) =>
  table(
    ['Field / Control', 'Required', 'What it does'],
    rows.map((r) => [r[0], r[1], r[2]]),
    'Field reference'
  );

export const permissionTable = (rows) =>
  table(
    ['Permission / role need', 'Why it matters'],
    rows,
    'Permissions'
  );

/**
 * Standard enterprise guide section pack.
 * Pass overrides for any section; omit with null to skip.
 */
export function buildGuide({
  id,
  title,
  moduleId,
  summary,
  keywords = [],
  pathHint = '',
  related = [],
  overview,
  videos,
  beforeYouBegin,
  steps: stepBlocks,
  screens,
  workflow: workflowBlock,
  businessLogic,
  tips,
  bestPractices,
  commonMistakes,
  troubleshooting,
  faqs,
  extraSections = [],
}) {
  const sections = [];

  const push = (sid, stitle, blocks) => {
    if (!blocks || (Array.isArray(blocks) && blocks.length === 0)) return;
    sections.push({
      id: sid,
      title: stitle,
      blocks: Array.isArray(blocks) ? blocks : [blocks],
    });
  };

  const videoBlocks = videos
    ? Array.isArray(videos)
      ? videos.filter(Boolean)
      : [videos]
    : null;

  push('overview', 'Overview', overview);
  push('watch-and-learn', 'Watch & Learn', videoBlocks);
  push('before-you-begin', 'Before You Begin', beforeYouBegin);
  push('step-by-step', 'Step-by-Step Instructions', stepBlocks);
  push('screens', 'Screens Explained', screens);
  push('workflow', 'Workflow', workflowBlock);
  push('business-logic', 'Business Logic', businessLogic);
  push('tips', 'Tips', tips);
  push('best-practices', 'Best Practices', bestPractices);
  push('common-mistakes', 'Common Mistakes', commonMistakes);
  push('troubleshooting', 'Troubleshooting', troubleshooting);
  push('faqs', 'FAQs', faqs);

  extraSections.forEach((s) => {
    if (s?.id && s?.title && s?.blocks) sections.push(s);
  });

  return {
    id,
    title,
    moduleId,
    summary,
    keywords,
    pathHint,
    related,
    sections,
  };
}

export function flattenText(blocks = []) {
  return blocks
    .map((b) => {
      if (!b) return '';
      if (b.text) return b.text;
      if (b.title) return b.title;
      if (b.body) return b.body;
      if (b.label) return b.label;
      if (b.caption) return b.caption;
      if (b.youtubeId) return [b.title, b.caption, b.youtubeId].filter(Boolean).join(' ');
      if (b.items) {
        return b.items
          .map((it) =>
            typeof it === 'string'
              ? it
              : [it.text, it.title, it.q, it.a, it.body].filter(Boolean).join(' ')
          )
          .join(' ');
      }
      if (b.nodes) return b.nodes.join(' ');
      if (b.headers) return [...b.headers, ...(b.rows || []).flat()].join(' ');
      return '';
    })
    .join(' ');
}
